import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { helpTopicForPath } from './helpTopics';
import { DocsHelp } from './DocsHelp';
import { useAppStore } from '../state/store';
import { useFeature } from '../hooks/useFeature';
import { useHelpActivity } from '../hooks/useHelpActivity';
import type { FeatureKey } from '../generated/contract';
import './HelpSidebar.css';

// Context-aware help panel: a floating ? button that expands into the help
// for the page you are on. Mounted in ProjectLayout (every project page) and
// on the projects list. The content and the route→topic mapping live in
// helpTopics.ts; this file is only the rendering. It reads the current route and
// shows a short summary + practical tips for that page, a deep link into the
// most relevant manual chapter, and a link to the full manual. Manual links
// open in a new tab (the /manual routes full-load fine as an SPA entry).
//
// Chapter slugs must match MANUAL_CHAPTERS in src/manual/index.ts — they are
// deep-linked as /manual/<slug>.
//
// Where the deployment serves help from the user manual on OpenD (capability
// help-docs, docs/plans/opend-help.md), the panel shows the manual's own
// topic for the page instead, for the release the workspace is on
// (DocsHelp); it falls back to the summary below if the docs cannot answer.

/** The deployment capability that serves help from the docs site. */
export const CAPABILITY_HELP_DOCS = 'help-docs';

/** The release gate on it: the member's channel must have reached it too. */
export const MANUAL_HELP_FEATURE: FeatureKey = 'manual-help';

/** The gate on help following the section, button and tab in use. */
export const HELP_ACTIVITY_FEATURE: FeatureKey = 'help-activity';

interface HelpSidebarProps {
  /** Controlled open state. Every shell keeps it now, with the ? button in
   *  its own bar beside the notification bell; omitting it falls back to a
   *  floating button, which no shell in the app asks for any more. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const HelpSidebar: React.FC<HelpSidebarProps> = ({ open, onOpenChange }) => {
  const [ownExpanded, setOwnExpanded] = useState(false);
  const controlled = open !== undefined;
  const isExpanded = controlled ? open : ownExpanded;
  const setIsExpanded = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    if (!controlled) setOwnExpanded(next);
  };
  const location = useLocation();
  const topic = helpTopicForPath(location.pathname);
  // Help from the docs site only where the server says it has one: unlike
  // other capabilities, an unknown answer means no.
  const docsOffered = useAppStore((s) => s.capabilities?.includes(CAPABILITY_HELP_DOCS) ?? false);
  const orgId = useAppStore((s) => s.activeOrgId);
  const [docsFailed, setDocsFailed] = useState(false);
  useEffect(() => setDocsFailed(false), [orgId]);
  const onDocsUnavailable = useCallback(() => setDocsFailed(true), []);
  const manualHelp = useFeature(MANUAL_HELP_FEATURE);
  const showDocs = docsOffered && manualHelp && !!orgId && !docsFailed;
  // What the reader is doing on the page, tracked while the panel is closed
  // too, so that opening it lands on the section they were working in.
  const helpActivity = useFeature(HELP_ACTIVITY_FEATURE);
  const activity = useHelpActivity(showDocs && helpActivity, location.pathname);

  return (
    // data-help-ui: what happens in the panel is not what the reader is
    // doing on the page (useHelpActivity).
    <div className={`help-sidebar ${isExpanded ? 'expanded' : 'collapsed'}`} data-help-ui="">
      {/* Controlled by the shell's own ? button, beside the notification
          bell. A floating button was the old arrangement and it cost the
          bottom-right corner of every page: on a phone it landed on the
          composer, and on a desktop it sat on whatever the page put there —
          the V&V runs table's Complete and Abort buttons, among others.
          Reserving that corner instead just moved the cost to a dead strip
          along the bottom of every page, so the button moved to the bar
          where the other per-person controls already live. What remains
          here is the ✕ that closes the panel, drawn only while it is open
          and inside the panel's own area. */}
      {(!controlled || isExpanded) && (
        <button
          className="help-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? 'Collapse help' : 'Expand help'}
          aria-label={isExpanded ? 'Close help' : 'Help'}
        >
          {isExpanded ? '✕' : '?'}
        </button>
      )}

      {isExpanded && showDocs && (
        <div className="help-content">
          <DocsHelp
            orgId={orgId}
            path={location.pathname}
            onUnavailable={onDocsUnavailable}
            activity={activity}
          />
        </div>
      )}

      {isExpanded && !showDocs && (
        <div className="help-content">
          <h3>{topic.title}</h3>

          <section className="help-section">
            <p className="help-intro">{topic.summary}</p>
          </section>

          <section className="help-section">
            <h4>Tips</h4>
            <ul>
              {topic.tips.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </section>

          <section className="help-section">
            <h4>Learn more</h4>
            <div className="help-item">
              <a
                href={`/manual/${topic.chapter.slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {topic.chapter.title} →
              </a>
              <p>The manual chapter for this page (opens in a new tab).</p>
            </div>
            {topic.also?.map((extra) => (
              <div className="help-item" key={extra.slug}>
                <a href={`/manual/${extra.slug}`} target="_blank" rel="noopener noreferrer">
                  {extra.title} →
                </a>
                <p>Related chapter (opens in a new tab).</p>
              </div>
            ))}
            <a
              className="help-manual-link"
              href="/manual"
              target="_blank"
              rel="noopener"
            >
              Full manual →
            </a>
          </section>
        </div>
      )}
    </div>
  );
};

export default HelpSidebar;
